import { parseTimeInput } from "./support/time_input";
import {Controller} from "@hotwired/stimulus"
import JustValidate from "just-validate"
import Swal from "sweetalert2"
import { partySetupRequest, signInForPartySetup, SETUP_SIGN_IN_MESSAGE } from "./support/party_setup_request"

export default class extends Controller {
    static targets = ["hostContainer", "childContainer", "childEntry", "form", "saveStatus", "saveMessage", "retrySave", "signInRecovery"]
    static values = {childCount: Number, draftId: String, draftSgid: String, anonymous: Boolean, invitationSlug: String, fields: Object, venuePartnerSlug: String, context: { type: String, default: "draft" }, // "draft" | "party"
        partyToken: String }
    ordinalWords = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth", "Eleventh", "Twelfth"];
    _pendingJVFields = [];
    _registerFieldValidation(selector, rules, opts) {
        if (this.validator) {
            this.validator.addField(selector, rules, opts);
        } else {
            (this._pendingJVFields ||= []).push({ sel: selector, rules, opts });
        }
    }

    connect() {
        if (this._connected) return;      // guard double init (prevents double listeners)
        this._connected = true;
        this._disconnected = false;
        this._leaving = false;
        this._goingBack = false;
        this._saveRevision = (this._saveRevision || 0) + 1;
        this._restoreFormInteraction();

        this.formTarget.setAttribute('novalidate', 'novalidate');

        // PREVENT any autosave until we've hydrated everything
        this._suspendAutosave = true;

        this._onKeydown = (ev) => {
            // prevent Enter from submitting anywhere except textarea
            if (ev.key === 'Enter' && !(ev.target instanceof HTMLTextAreaElement)) {
                ev.preventDefault();
            }
        };
        this.formTarget.addEventListener('keydown', this._onKeydown, true);

        this._isSubmitting = false;

        // No manual submit listener: JustValidate handles submission via onSuccess.

        this.childCountValue = this.childContainerTarget.children.length;
        if (this.hasVenuePartnerSlugValue && this.venuePartnerSlugValue) {
            this.initVenueLocation();
        } else {
            this.initPlacesNew();
        }
        // live revalidation for start/end relationship
        const dEl = document.getElementById('text-date');
        const sEl = document.getElementById('startTime');
        const eEl = document.getElementById('endTime');
        const reval = () => { try { this.validator?.revalidateField?.('#endTime'); } catch(_) {} };
        dEl?.addEventListener('change', reval);
        sEl?.addEventListener('input', reval);
        eEl?.addEventListener('input', reval);
        if (this.hasFieldsValue && this.fieldsValue && Object.keys(this.fieldsValue).length) {
            this._hydrateFromFields(this.fieldsValue);
        }

        // Now that the form is hydrated, set up debounced autosave and listeners
        this._saveDebounced = this._debounce(this._save.bind(this), 800);

        this._onFormChange = () => {
            if (this._suspendAutosave || this._isSubmitting || this._leaving) return;
            this._saveRevision++;
            this._setSaveStatus('pending');
            if (this._goingBack && this._autosavePromise) this._autosaveQueued = true;
            this._saveDebounced();
        };
        this.formTarget.addEventListener('input', this._onFormChange);
        this.formTarget.addEventListener('change', this._onFormChange);

        // Ensure pending array exists before validation init
        this._pendingJVFields ||= [];
        // Initialize validation after hydration and listeners
        this.initValidation();

        // Finally, allow autosave
        this._suspendAutosave = false;

    }

    _hydrateFromFields(payload) {
        const f = payload || {};
        const party   = f.party || {};
        const hosts   = f.hosts || {};
        const kids    = Array.isArray(f.children) ? f.children : [];
        const addr    = (f.location && f.location.address) ? f.location.address : {};

        // Avoid autosave thrash while setting values
        this._suspendAutosave = true;

        // --- Basic info ---
        const setVal = (id, v) => { const el = document.getElementById(id); if (el) el.value = v || ""; };

        setVal("text-title", party.title);
        setVal("hostName",   hosts.primary_name || party.hosted_by);

        // Cohosts
        // wipe any extra cohost rows first
        this.hostContainerTarget.querySelectorAll('[id^="host_entry_"]').forEach(row => row.remove());
        if (Array.isArray(hosts.cohosts) && hosts.cohosts.length) {
            hosts.cohosts.forEach((email) => {
                const { input } = this.addHost(new Event("noop")) || {};
                if (input) input.value = email;
            });
        }

        // --- Celebrate for (children) ---
        // Reset to just the first child block in DOM
        this.childContainerTarget.querySelectorAll('[data-invitation-form-target="childEntry"]').forEach((block, i) => {
            if (i > 0) block.remove();
        });
        const first = kids[0] || {};
        setVal("celebrateForFirstChild", first.first_name);
        setVal("birthdayDateFirstChild", first.birth_date); // YYYY-MM-DD; flatpickr can parse
        const g1 = document.getElementById("genderFirstChild");
        if (g1) {
            g1.value = first.gender || "";
            if (first.gender) g1.dispatchEvent(new Event("change", { bubbles: true }));
        }

        // Extra children
        for (let i = 1; i < kids.length; i++) {
            this.addChild(new Event("noop"));
            const k = kids[i];
            const nameId = `celebrateForChild_${i}`;
            const bdayId = `birthdayDateChild_${i}`;
            const genId  = `genderChild_${i}`;
            setVal(nameId, k.first_name);
            setVal(bdayId, k.birth_date);
            const sel = document.getElementById(genId);
            if (sel) {
                sel.value = k.gender || "";
                if (k.gender) sel.dispatchEvent(new Event("change", { bubbles: true }));
            }
        }

        // --- Date & Time ---
        // party_start/party_end are ISO "YYYY-MM-DDTHH:MM:SS"
        const parseISO = (iso) => {
            if (!iso) return null;
            const d = new Date(iso);
            return isNaN(d) ? null : d;
        };


        const start = parseISO(party.party_start);
        const end   = parseISO(party.party_end);

        if (this.isMobile()) {
            // On mobile, we need strict machine-readable formats (YYYY-MM-DD and HH:mm)
            const fmtDateMachine = (d) => {
                if (!d) return "";
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            };

            const fmtTimeMachine = (d) => {
                if (!d) return "";
                const hh = String(d.getHours()).padStart(2, '0');
                const mi = String(d.getMinutes()).padStart(2, '0');
                return `${hh}:${mi}`;
            };

            setVal("text-date", fmtDateMachine(start));
            setVal("startTime", fmtTimeMachine(start));
            if (end && start && !(end.getHours() === 23 && end.getMinutes() === 59)) {
                setVal("endTime", fmtTimeMachine(end));
            }

        } else {
            // Match flatpickr's English format regardless of the browser's locale.
            // Localized dates/times (e.g. Canadian "p.m.") can otherwise break initialization.
            const fmtDateHuman = (d) => d?.toLocaleDateString("en-US", { weekday:"long", year:"numeric", month:"long", day:"numeric" }) || "";
            const fmtTimeHuman = (d) => d?.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true }) || "";

            setVal("text-date", fmtDateHuman(start));
            setVal("startTime", fmtTimeHuman(start));
            if (end && start && !(end.getHours() === 23 && end.getMinutes() === 59)) {
                setVal("endTime", fmtTimeHuman(end));
            }
        }

        // --- Address ---
        if (this.hasVenuePartnerSlugValue && this.venuePartnerSlugValue) {
            const venueLocation = document.getElementById("venueLocation");
            if (venueLocation && f.location?.venue_location_slug) {
                venueLocation.value = f.location.venue_location_slug;
            }
            this._applyVenueLocation();
        } else {
            setVal("text-address-line2", addr.street_address);
            setVal("city", addr.city);
            const prov = document.getElementById("province");
            if (prov) {
                // We stored province_state_id (e.g. "62") — select by value
                // Use helper to select and fire event, passing country code for consistency
                this._selectProvinceOption(prov, addr.province_state_id || "", addr.country_code || "");
            }
            setVal("zipCode", addr.postal_zip);
        }

        setVal("waiverLink", party.waiver_link);

        this._suspendAutosave = false;

    }

    isMobile() {
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }

    async _waitForGooglePlaces() {
        // already loaded?
        if (window.google?.maps?.places) return;

        // try to find an existing script tag (your layout adds it via content_for)
        let sc = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

        // if no script found, inject one (keeps working locally or if partials change)
        if (!sc) {
            const key = document.querySelector('meta[name="google-maps-api-key"]')?.content
                || "<%= ENV['GOOGLE_MAPS_API_KEY'] %>"; // optional; safe fallback
            sc = document.createElement("script");
            sc.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&loading=async`;
            sc.async = true;
            sc.defer = true;
            sc.setAttribute("data-ea-gmaps", "1");
            document.head.appendChild(sc);
        }

        // wait for script load
        await new Promise((resolve, reject) => {
            if (sc.dataset.loaded === "1") return resolve();
            sc.addEventListener("load", () => { sc.dataset.loaded = "1"; resolve(); }, { once: true });
            sc.addEventListener("error", () => reject(new Error("Google Maps script failed to load")), { once: true });
            // if it was already loaded before we attached listeners:
            if (document.readyState === "complete" && window.google?.maps) {
                sc.dataset.loaded = "1"; resolve();
            }
        });

        // sometimes maps scaffolds first, then places lands slightly after; try importLibrary
        if (google.maps?.importLibrary) {
            try { await google.maps.importLibrary("places"); } catch (_) {}
        }

        // final sanity wait loop (handles slow init)
        for (let i = 0; i < 40 && !window.google?.maps?.places; i++) {
            // ~2s max
            await new Promise(r => setTimeout(r, 50));
        }
        if (!window.google?.maps?.places) throw new Error("Google Places failed to initialize");
    }

    // back button
    async back(event) {
        event?.preventDefault();
        if (this._isSubmitting || this._leaving || this._goingBack) return;
        this._goingBack = true;
        this._saveDebounced?.cancel();
        const saved = await this._save();
        this._goingBack = false;
        if (!saved || this._disconnected) return;
        this._leaving = true;
        const sgid = this.anonymousValue
            ? `draft_ref=${encodeURIComponent(this.draftIdValue)}`
            : (this.hasDraftSgidValue && this.draftSgidValue
                ? `draft_sgid=${encodeURIComponent(this.draftSgidValue)}`
                : `draft_id=${encodeURIComponent(this.draftIdValue)}`);
        const slug = this.hasInvitationSlugValue ? `&invitation=${encodeURIComponent(this.invitationSlugValue)}` : "";
        // adapt to your actual editor route:
        window.location.assign(`/party/editor?${sgid}${slug}`);
    }

    initValidation() {
        if (this.validator) this.validator.destroy();

        this.validator = new JustValidate(this.formTarget, {
            errorFieldCssClass: 'just-validate-error-field',
            errorLabelCssClass: 'just-validate-error-label',
            validateBeforeSubmitting: true,
            lockForm: false,
            focusInvalidField: true,
        });

        this.validator.onSuccess((event) => {
            event?.preventDefault();
            event?.stopPropagation?.();
            this._submit(event);
        });
        this.validator.onFail((fields) => {
            if (this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('details_validation_blocked', 'validation');
            const first = fields?.[0]?.elem || this.formTarget.querySelector('.just-validate-error-field');
            if (first?.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            this._toast('Please fix the highlighted fields', 'error');
        });

        // Text inputs
        this.validator.addField('#text-title', [{ rule: 'required', errorMessage: 'The event title is required.' }]);
        this.validator.addField('#hostName',   [{ rule: 'required', errorMessage: 'The host name is required.' }]);

        // First child
        this.validator.addField('#celebrateForFirstChild', [{ rule: 'required', errorMessage: "The celebrant's name is required." }]);
        this.validator.addField('#birthdayDateFirstChild', [{ rule: 'required', errorMessage: 'The birthday is required.' }]);
        this.validator.addField('#genderFirstChild',
            [{ rule: 'required', errorMessage: 'The gender is required.' }],
            { errorLabelContainer: '#genderFirstChildError' }
        );



        this.validator.addField('#text-date', [{ rule: 'required', errorMessage: 'The starting date is required.' }]);
        this.validator.addField('#startTime', [
            { rule: 'required', errorMessage: 'The start time is required.' },
            {
                validator: (value) => !value || this._parseTimeToHM(value) !== null,
                errorMessage: 'Enter a valid start time.'
            }
        ]);


        this.validator.addField('#endTime', [
            {
                validator: (value) => !value || this._parseTimeToHM(value) !== null,
                errorMessage: 'Enter a valid end time.'
            },
            {
                validator: (value) => {
                    const start = this._parseTimeToHM(document.getElementById('startTime')?.value || '');
                    const end = this._parseTimeToHM(value);
                    // Required/format rules handle missing or invalid values; the date is independent.
                    if (!start || !end) return true;
                    return end.H * 60 + end.M >= start.H * 60 + start.M;
                },
                errorMessage: 'End time must be after the start time.'
            }
        ]);

        // Address
        if (this.hasVenuePartnerSlugValue && this.venuePartnerSlugValue) {
            this.validator.addField('#venueLocation',
                [{ rule: 'required', errorMessage: 'Please choose your party location.' }],
                { errorLabelContainer: '#venueLocationError' }
            );
        } else {
            this.validator.addField('#text-address-line2', [{ rule: 'required', errorMessage: 'Address is required.' }]);
            this.validator.addField('#city',
                [{ rule: 'required', errorMessage: 'Please enter a city.' }],
                { errorLabelContainer: '#cityError' }
            );
            this.validator.addField('#province',
                [{ rule: 'required', errorMessage: 'Please select a state/county.' }],
                { errorLabelContainer: '#stateError' }
            );
            this.validator.addField('#zipCode', [{ rule: 'required', errorMessage: 'The postal/zip code is required.' }]);
        }

        // Register any fields that were queued before JV existed (e.g., co-hosts added during hydration)
        if (this._pendingJVFields && this._pendingJVFields.length) {
            try {
                this._pendingJVFields.forEach(({ sel, rules, opts }) => {
                    this.validator.addField(sel, rules, opts);
                });
            } catch (_) { /* no-op */ }
            this._pendingJVFields = [];
        }
    }

    disconnect() {
        this._disconnected = true;
        this._saveRevision++;
        this._autosaveQueued = false;
        this._saveDebounced?.cancel();
        this._restoreFormInteraction();
        if (this._onFormChange) {
            this.formTarget.removeEventListener('input', this._onFormChange);
            this.formTarget.removeEventListener('change', this._onFormChange);
        }
        if (this._onKeydown) this.formTarget.removeEventListener('keydown', this._onKeydown, true);
        if (this._blockSubmit) this.formTarget.removeEventListener('submit', this._blockSubmit, true);
        this._connected = false;
    }

    async initPlacesNew() {
        try {
            await this._waitForGooglePlaces();

            const input = document.getElementById("text-address-line2");
            if (!input) return;

            // Create suggestion container
            const list = document.createElement("div");
            list.id = "ea-places-suggestions";
            list.setAttribute("role", "listbox");
            list.style.display = "none";
            input.parentElement.style.position = "relative";
            input.parentElement.appendChild(list);

            const acService = new google.maps.places.AutocompleteService();
            const sessionToken = new google.maps.places.AutocompleteSessionToken();

            const renderPredictions = (preds=[]) => {
                list.innerHTML = "";
                if (!preds.length) { list.style.display = "none"; return; }
                preds.forEach(p => {
                    const item = document.createElement("button");
                    item.type = "button";
                    item.className = "ea-places-item";
                    item.setAttribute("role", "option");
                    const main = p.structured_formatting?.main_text || p.description;
                    const secondary = p.structured_formatting?.secondary_text || '';
                    item.textContent = secondary ? `${main}, ${secondary}` : main;
                    item.addEventListener("click", () => this._selectPrediction(p, sessionToken, input, list));
                    list.appendChild(item);
                });
                list.style.display = "block";
            };

            const fetchPredictions = this._debounce((q) => {
                this._lastAddressQuery = q; // remember typed text
                if (!q || q.length < 3) { renderPredictions([]); return; }

                const baseOpts = {
                    input: q,
                    sessionToken,
                    includeQueryPredictions: false,
                    componentRestrictions: { country: ["ca", "us"] }
                };

                // First: real street addresses (prefer numbers)
                acService.getPlacePredictions({ ...baseOpts, types: ["street_address"] }, (preds1, status1) => {
                    const ok1 = status1 === google.maps.places.PlacesServiceStatus.OK && Array.isArray(preds1);

                    // If nothing: try venues/businesses
                    if (!ok1 || !preds1.length) {
                        acService.getPlacePredictions({ ...baseOpts, types: ["establishment"] }, (preds2, status2) => {
                            const ok2 = status2 === google.maps.places.PlacesServiceStatus.OK && Array.isArray(preds2);
                            if (!ok2) { renderPredictions([]); return; }
                            const seen = new Set();
                            const uniq = preds2.filter(p => {
                                const k = p.place_id || p.description;
                                if (seen.has(k)) return false;
                                seen.add(k);
                                return true;
                            });
                            renderPredictions(uniq.slice(0, 6));
                        });
                        return;
                    }

                    const seen = new Set();
                    const uniq = preds1.filter(p => {
                        const k = p.place_id || p.description;
                        if (seen.has(k)) return false;
                        seen.add(k);
                        return true;
                    });
                    renderPredictions(uniq.slice(0, 6));
                });
            }, 180);

            // events
            input.addEventListener("input", (e) => {
                if (this._suspendAutosave) return;
                fetchPredictions(e.target.value.trim());
            });
            input.addEventListener("focus", () => {
                if (list.childElementCount) list.style.display = "block";
            });
            document.addEventListener("click", (ev) => {
                if (!list.contains(ev.target) && ev.target !== input) list.style.display = "none";
            });
        } catch (e) {
            console.warn("Custom Places init failed:", e);
        }
    }

    initVenueLocation() {
        const select = document.getElementById("venueLocation");
        if (!select) return;

        select.addEventListener("change", () => this._applyVenueLocation({ persist: true }));
        this._applyVenueLocation();
    }

    _applyVenueLocation({ persist = false } = {}) {
        const select = document.getElementById("venueLocation");
        const option = select?.selectedOptions?.[0];
        if (!select || !option) return;

        const setValue = (id, value) => {
            const element = document.getElementById(id);
            if (element) element.value = value || "";
        };

        setValue("text-address-line2", option.dataset.address);
        setValue("city", option.dataset.city);
        setValue("province", option.dataset.provinceStateId);
        setValue("zipCode", option.dataset.postalZip);

        const address = document.getElementById("venueLocationAddress");
        if (address) address.textContent = option.dataset.displayAddress || "";

        try { this.validator?.revalidateField?.('#venueLocation'); } catch (_) {}
        if (persist && !this._suspendAutosave) this._saveDebounced?.();
    }

    _extractLeadingNumber(str) {
        if (!str) return "";
        const m = String(str).match(/^\s*(\d+[^\s,]*)\b/); // 23, 23A, 23-25 etc.
        return m ? m[1] : "";
    }

// Called when user clicks a suggestion
    async _selectPrediction(prediction, sessionToken, streetEl, listEl) {
        listEl.style.display = "none";
        try {
            // New Places Details: build Place by id then fetch fields
            // eslint-disable-next-line new-cap
            const place = new google.maps.places.Place({ id: prediction.place_id, requestedLanguage: "en" });
            await place.fetchFields({
                fields: ["addressComponents", "formattedAddress", "displayName"],
                sessionToken
            });

            const comps = (place.addressComponents || []).map(c => ({
                types: c.types || [],
                long:  c.longText || c.long_name,
                short: c.shortText || c.short_name
            }));
            const get = (type) => comps.find(c => c.types.includes(type)) || {};

            const streetNumber = (get("street_number").long || "").trim();
            const route        = (get("route").long || "").trim();
            const city         = (get("locality").long
                || get("postal_town").long
                || get("sublocality").long
                || get("administrative_area_level_2").long
                || "").trim();
            const provinceCode = (get("administrative_area_level_1").short || get("administrative_area_level_1").long || "").trim();
            const postal       = (get("postal_code").long || "").trim();

            const countryCode  = (get("country").short || "").trim();

            // Prefer the official displayName; fallback to prediction main_text when it is not a number-first string
            const predictedMain = prediction?.structured_formatting?.main_text || "";
            const venueFromPrediction = /^\s*\d/.test(predictedMain) ? "" : predictedMain; // ignore if looks like "123 Street"
            const venueName = (place.displayName && place.displayName.text) ? place.displayName.text : venueFromPrediction;

            // Try to recover number when components don't have it
            let number = streetNumber;
            if (!number) {
                // 1) prediction main_text   e.g. "23 Front St W"
                number = this._extractLeadingNumber(prediction?.structured_formatting?.main_text);
            }
            if (!number) {
                // 2) full prediction text   e.g. "23 Front St W, Toronto, ON…"
                number = this._extractLeadingNumber(prediction?.description);
            }
            if (!number) {
                // 3) the user’s typed query e.g. "23 Front West Street"
                number = this._extractLeadingNumber(this._lastAddressQuery);
            }
            if (!number && place.formattedAddress) {
                // 4) formatted address first part
                number = this._extractLeadingNumber(place.formattedAddress.split(",")[0]);
            }

            const subpremise   = (get("subpremise").long || "").trim();  // unit/apt

            // Build canonical street number+route for storage/fallbacks
            const baseStreet = [number, route].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();

            // Preferred user-visible label: keep venue name if available, similar to Google list display.
            // Examples:
            //   "Active Kids Zone, Alness Street"
            //   "Community Centre, 123 King St W"
            //   If no venue, fall back to "123 King St W" or first part of formatted address.
            let streetLabel = "";
            if (venueName && route) {
                streetLabel = `${venueName}, ${route}`;
            } else if (venueName && baseStreet) {
                streetLabel = `${venueName}, ${baseStreet}`;
            } else if (baseStreet) {
                streetLabel = baseStreet;
            }

            // Append unit/apartment politely if present (e.g., "Suite 200 — Active Kids Zone, Alness Street")
            if (subpremise && streetLabel) {
                streetLabel = `${subpremise} — ${streetLabel}`;
            } else if (subpremise) {
                streetLabel = subpremise;
            }

            // Final fallbacks if we still failed to build a label
            if (!streetLabel && place.formattedAddress) streetLabel = place.formattedAddress.split(",")[0].trim();
            if (!streetLabel && prediction?.structured_formatting?.main_text) streetLabel = prediction.structured_formatting.main_text.split(",")[0].trim();
            if (!streetLabel && prediction?.description) streetLabel = prediction.description.split(",")[0].trim();

            // Fill your visible inputs (suppress autosave while bulk setting)
            const autosaveWasSuspended = Boolean(this._suspendAutosave);
            this._suspendAutosave = true;
            try {
                streetEl.value = streetLabel || "";

                const cityEl = document.getElementById("city");
                if (cityEl) cityEl.value = city || "";

                const zipEl = document.getElementById("zipCode");
                if (zipEl) zipEl.value = postal || "";

                const provEl = document.getElementById("province");
                this._selectProvinceOption(provEl, provinceCode, countryCode);

                this._touchAndRevalidate('#text-address-line2');
                this._touchAndRevalidate('#city');
                this._touchAndRevalidate('#province');
                this._touchAndRevalidate('#zipCode');
            } finally {
                // A widget/validation error must not disable subsequent edits.
                this._suspendAutosave = autosaveWasSuspended;
            }
            if (!autosaveWasSuspended) this._saveDebounced();
        } catch (e) {
            console.warn("Fetching place details failed:", e);
        } finally {
            listEl.style.display = "none";
            listEl.replaceChildren();
        }
    }

    _provinceLongName(code, country) {
        const CA = {
            AB:"Alberta", BC:"British Columbia", MB:"Manitoba", NB:"New Brunswick",
            NL:"Newfoundland and Labrador", NS:"Nova Scotia", NT:"Northwest Territories",
            NU:"Nunavut", ON:"Ontario", PE:"Prince Edward Island", QC:"Quebec",
            SK:"Saskatchewan", YT:"Yukon"
        };
        const US = {
            AL:"Alabama", AK:"Alaska", AZ:"Arizona", AR:"Arkansas", CA:"California", CO:"Colorado",
            CT:"Connecticut", DE:"Delaware", FL:"Florida", GA:"Georgia", HI:"Hawaii", ID:"Idaho",
            IL:"Illinois", IN:"Indiana", IA:"Iowa", KS:"Kansas", KY:"Kentucky", LA:"Louisiana",
            ME:"Maine", MD:"Maryland", MA:"Massachusetts", MI:"Michigan", MN:"Minnesota",
            MS:"Mississippi", MO:"Missouri", MT:"Montana", NE:"Nebraska", NV:"Nevada",
            NH:"New Hampshire", NJ:"New Jersey", NM:"New Mexico", NY:"New York", NC:"North Carolina",
            ND:"North Dakota", OH:"Ohio", OK:"Oklahoma", OR:"Oregon", PA:"Pennsylvania",
            RI:"Rhode Island", SC:"South Carolina", SD:"South Dakota", TN:"Tennessee",
            TX:"Texas", UT:"Utah", VT:"Vermont", VA:"Virginia", WA:"Washington", WV:"West Virginia",
            WI:"Wisconsin", WY:"Wyoming", DC:"District of Columbia"
        };
        const map = (String(country || "CA").toUpperCase() === "US") ? US : CA;  // default to CA
        const key = String(code || "").toUpperCase();
        return String(map[key] || key);
    }

// 1) helper: fire input/change + try to re-run JV for a field
    _touchAndRevalidate(selector) {
        const el = document.querySelector(selector);
        if (!el) return;
        // make JV think the user interacted
        el.dispatchEvent(new Event('input',  { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));

        // clear custom containers we use
        if (selector === '#city')   { const c = document.getElementById('cityError');  if (c) c.textContent = ''; }
        if (selector === '#province'){ const c = document.getElementById('stateError'); if (c) c.textContent = ''; }

        // if your JV version supports it, this will immediately re-check the field
        try { this.validator?.revalidateField?.(selector); } catch (_) {}
    }

// Keep the native value and Choices' visible selection in sync.
    _selectProvinceOption(selectEl, provinceCode, countryCode) {
        if (!selectEl) return;
        const codeStr  = String(provinceCode ?? "").trim();
        if (!codeStr) return;
        const country  = String(countryCode ?? "CA").toUpperCase();
        const longName = String(this._provinceLongName(codeStr, country) || "").trim();
        const normalize = (value) => String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

        // Find the match before changing selection. Clearing every option can
        // make a single-select fall back to the first province (Alberta).
        const option = Array.from(selectEl.options).find(opt => {
            const values = [normalize(opt.value), normalize(opt.textContent)];
            return values.includes(normalize(codeStr)) || values.includes(normalize(longName));
        });
        if (!option) return;

        const choices = this.application.getControllerForElementAndIdentifier(selectEl, "choices")?.choices;
        if (choices) {
            choices.setChoiceByValue(option.value);
        } else {
            // Hydration can run before the Choices controller connects.
            selectEl.value = option.value;
        }
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    addHost(event) {
        event.preventDefault();
        const idx = this.hostContainerTarget.children.length;
        const id  = `cohostEmail_${idx}`;
        const html = `
    <div class="row g-3 mb-3" id="host_entry_${idx}">
      <div class="col-md-12 d-flex gap-2">
        <div class="w-100">
          <label for="${id}" class="p3 visually-hidden">Co-host email (${idx + 1}) *</label>
          <input type="email" class="form-control me-2" id="${id}" name="${id}" placeholder="name@example.com" required>
        </div>
        <div class="d-flex align-items-center">
          <a href="#" class="text-danger" data-action="click->invitation-form#removeHost" data-target-id="host_entry_${idx}">
            <i class="bi bi-x-lg"></i>
          </a>
        </div>
      </div>
    </div>
  `;
        this.hostContainerTarget.insertAdjacentHTML('beforeend', html);

        // validate email format
        this._registerFieldValidation(`#${id}`, [
            { rule: 'required', errorMessage: 'Co-host email is required.' },
            { rule: 'email', errorMessage: 'Enter a valid email address.' }
        ]);

        // return created nodes so callers can set values without guessing ids
        const row   = this.hostContainerTarget.querySelector(`#host_entry_${idx}`);
        const input = row?.querySelector(`#${id}`);
        return { row, input, id };

    }

    removeHost(event) {
        event.preventDefault();
        const targetId = event.currentTarget.dataset.targetId;
        const row = document.getElementById(targetId);
        if (!row) return;

        const input = row.querySelector('input[id^="cohostEmail_"]');
        if (input) {
            try { this.validator.removeField(`#${input.id}`); } catch (_) {}
        }
        row.remove();
    }


    addChild(event) {
        event.preventDefault();
        this.childCountValue++;
        const childNumber = this.childCountValue;
        const newChildIndex = childNumber - 1;

        const childOrdinalWord = this.ordinalWords[newChildIndex] || `Child ${childNumber}`;

        const newChildHtml = `
            <div class="mb-2 mt-4" data-invitation-form-target="childEntry" id="childEntry_${newChildIndex}">               
                <div class="mb-2">
                       <div class="d-flex justify-content-between">                                 
                        <label for="celebrateForChild_${newChildIndex}" class="p3">${childOrdinalWord} child *</label>
                        <a href="#" class="text-danger" data-action="click->invitation-form#removeChild" data-target-id="childEntry_${newChildIndex}">
                            <i class="bi bi-x-lg"></i>
                        </a>      
                     </div>                   
                    <input type="text" class="form-control" id="celebrateForChild_${newChildIndex}" name="celebrateForChild_${newChildIndex}" placeholder="Celebrant's name (full name)" required>
                </div>
                <div class="row g-3">
                    <div class="col-md-6">
                        <label for="birthdayDateChild_${newChildIndex}" class="d-none p3">Birthday *</label>
                        <div class="input-group input-group-icon">
                            <div style="position: relative;" class="w-100">
                                <input placeholder="Date of Birth" type="text" 
                                data-controller="flat-picker" data-flat-picker-max-date-value="today" 
                                data-flat-picker-disable-mobile="false" data-flat-picker-allow-input-value="false" 
                                class="form-control date-control" id="birthdayDateChild_${newChildIndex}" name="birthdayDateChild_${newChildIndex}" required>
                                <span aria-hidden="true" class="position-absolute top-50 end-0 translate-middle-y me-3 text-muted pe-none"><i class="bi bi-calendar4"></i></span>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <label for="genderChild_${newChildIndex}" class="d-none p3">Gender *</label>
                        <select  data-controller="choices"  class="form-select" id="genderChild_${newChildIndex}" name="genderChild_${newChildIndex}" required>
                            <option selected disabled value="">Gender</option>
                            <option value="Male">Boy</option>
                            <option value="Female">Girl</option>
                            <option value="Non-binary">Non-binary</option>
                            <option value="Rather not say">Rather not say</option>
                        </select>
                        <div id="genderChild_${newChildIndex}Error" class="custom-placed-error-message"></div>
                    </div>
                </div>
            </div>
        `;
        this.childContainerTarget.insertAdjacentHTML('beforeend', newChildHtml);

        this._registerFieldValidation(`#celebrateForChild_${newChildIndex}`, [
            { rule: 'required', errorMessage: "The celebrant's name is required." }
        ]);
        this._registerFieldValidation(`#birthdayDateChild_${newChildIndex}`, [
            { rule: 'required', errorMessage: 'The birthday is required.' }
        ]);
        this._registerFieldValidation(
            `#genderChild_${newChildIndex}`,
            [{ rule: 'required', errorMessage: 'The gender is required.' }],
            { errorLabelContainer: `#genderChild_${newChildIndex}Error` }
        );

        const gSel = document.getElementById(`genderChild_${newChildIndex}`);
        gSel?.addEventListener('change', (ev) => {
            if (ev.target.value) {
                const c = document.getElementById(`genderChild_${newChildIndex}Error`);
                if (c) c.textContent = '';
                ev.target.classList.remove('just-validate-error-field');
            }
        });
    }

    removeChild(event) {
        event.preventDefault();

        // Find the nearest child block regardless of its id/index
        const block = event.currentTarget.closest('[data-invitation-form-target="childEntry"]');
        if (!block) return;

        // Unregister JV rules for any inputs inside this block
        const ids = Array.from(block.querySelectorAll('input, select'))
            .map(el => el.id)
            .filter(Boolean);

        ids.forEach(id => { try { this.validator.removeField(`#${id}`); } catch (_) {} });

        // Remove block, reindex, and persist
        block.remove();
        this.childCountValue--;
        this.reindexChildren();
        this._persistChildrenAfterRemoval?.();
    }

    async _persistChildrenAfterRemoval() {
        // Use the same ordered, checked save path as other edits.
        this._saveRevision++;
        this._saveDebounced?.cancel();
        return this._save();
    }

    _collectFieldsStructured() {
        const fd = new FormData(this.formTarget);

        // helpers
        const str = (k) => (fd.get(k) || "").toString().trim();

        // date helpers (assume inputs are local)
        // with this local-safe version:
        const toISODateLocal = (v) => {
            if (!v) return null;
            const s = String(v).trim();
            // If it’s already YYYY-MM-DD, keep it as-is
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

            const d = new Date(s);
            if (isNaN(d)) return null;
            const yyyy = d.getFullYear();
            const mm   = String(d.getMonth() + 1).padStart(2, '0');
            const dd   = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };
        const combineDateTime = (d, t) => {
            if (!d && !t) return null;
            // flatpickr altFormat “h:i K” still yields a parseable string when allowInput is true
            // We’ll parse via Date using local timezone
            const dObj = d ? new Date(d) : new Date();
            let [hh, mm, ampm] = (t || "00:00").split(/[:\s]/);
            let H = parseInt(hh || "0", 10);
            const M = parseInt(mm || "0", 10);
            if ((/am|pm/i).test(ampm || "")) {
                const isPM = /pm/i.test(ampm);
                if (H === 12) H = isPM ? 12 : 0; else H = isPM ? H + 12 : H;
            }
            const yyyy = dObj.getFullYear();
            const MM   = String(dObj.getMonth() + 1).padStart(2, "0");
            const DD   = String(dObj.getDate()).padStart(2, "0");
            const HH   = String(H).padStart(2, "0");
            const MI   = String(M).padStart(2, "0");
            return `${yyyy}-${MM}-${DD}T${HH}:${MI}:00`;
        };

        // party title + host
        const title      = str("eventName");
        const hosted_by  = str("hostName");

        const priorCharityId =
            (this.hasFieldsValue && this.fieldsValue?.party?.charity_id)
                ? String(this.fieldsValue.party.charity_id)
                : undefined;

        // cohost emails
        const cohosts = [];
        for (const [k, v] of fd.entries()) {
            if (/^cohostEmail_\d+$/.test(k) && v) cohosts.push(v.toString().trim());
        }

        // children
        const children = [];
        // first child (fixed ids)
        {
            const first = str("celebrateForChild_0");
            const bday  = str("birthdayDateChild_0");
            const gen   = str("genderChild_0");
            if (first || bday || gen) {
                children.push({
                    first_name: first || "",
                    birth_date: toISODateLocal(bday),
                    gender: gen || ""
                });
            }
        }
        // dynamic children (added via controller)
        const dyn = Array.from(this.childContainerTarget.querySelectorAll('[data-invitation-form-target="childEntry"]'))
            .map((el, idx) => idx) // indices 0..n-1 (we already pushed 0)
            .slice(1);
        for (const i of dyn) {
            const name = str(`celebrateForChild_${i}`);
            const bday = str(`birthdayDateChild_${i}`);
            const gen  = str(`genderChild_${i}`);
            if (name || bday || gen) {
                children.push({
                    first_name: name || "",
                    birth_date: toISODateLocal(bday),
                    gender: gen || ""
                });
            }
        }

        // hosted_for (derive from first child’s name, but still store explicitly)
        const hosted_for = children[0]?.first_name || "";

        // date & time
        const startingDate = str('startingDate');
        const startTime    = str('startTime');
        const endTime      = str('endTime');

        const startISO = this._buildLocalISO(startingDate, startTime);
        let   endISO   = endTime ? this._buildLocalISO(startingDate, endTime) : null;
        if (!endISO && startISO) {
            endISO = this._buildEndOfDayISO(startingDate);
        }

        // address
        const address = {
            street_address: str("address"),
            city:           str("city"),
            province_state_id: str("province"), // your select_tag :province yields a code/value
            postal_zip:     str("zipCode")
        };

        const waiver_link = str("waiverLink");
        const venue_location_slug = str("venueLocation");

        return {
            party: {
                title,
                hosted_by,
                hosted_for,
                party_start: startISO,
                party_end: endISO,
                waiver_link,
                // preserve charity selection across steps
                ...(priorCharityId ? { charity_id: priorCharityId } : {})
            },
            hosts: {
                primary_name: hosted_by,
                cohosts
            },
            children,
            location: {
                address,
                ...(this.hasVenuePartnerSlugValue && this.venuePartnerSlugValue ? {
                    venue_partner_slug: this.venuePartnerSlugValue,
                    venue_location_slug
                } : {})
            }
        };
    }

    // ---- robust date/time helpers ----

// Try to parse a human date like "Thursday, August 28, 2025"
// Falls back to "August 28, 2025" if needed.
    _parseLocalDate(dStr) {
        if (!dStr) return null;
        let d = new Date(dStr);                         // may be NaN on Safari/etc
        if (isNaN(d)) {
            // take the trailing "Month DD, YYYY" part
            const alt = dStr.split(',').slice(-2).join(',').trim();
            d = new Date(alt);
        }
        if (isNaN(d)) return null;
        // zero out time
        d.setHours(0,0,0,0);
        return d;
    }

// Parse "10:05 PM", "10:05PM", "22:05", "9 PM", "9PM", "9"
    /**
     * Parses a time string (e.g., "8:30 PM" or "20:30") into hours and minutes.
     * @param {string} timeStr - The time string to parse.
     * @returns {{H: number, M: number}|null} - An object with hours and minutes, or null if invalid.
     */
    _parseTimeToHM(timeStr) {
        const time = parseTimeInput(timeStr);
        return time ? { H: time.H, M: time.M } : null;
    }

// Build "YYYY-MM-DDTHH:MM:00" from a human date string and a time string.
    _buildLocalISO(dStr, tStr) {
        const d = this._parseLocalDate(dStr);
        if (!d) return null;
        const hm = this._parseTimeToHM(tStr);
        if (!hm) return null;

        const dt = new Date(d);
        dt.setHours(hm.H, hm.M, 0, 0);

        // This formatting logic is correct.
        const yyyy = dt.getFullYear();
        const MM = String(dt.getMonth() + 1).padStart(2, '0');
        const DD = String(dt.getDate()).padStart(2, '0');
        const HH = String(dt.getHours()).padStart(2, '0');
        const MI = String(dt.getMinutes()).padStart(2, '0');
        return `${yyyy}-${MM}-${DD}T${HH}:${MI}:00`;
    }

// Build end-of-day for a given human date string: "YYYY-MM-DDT23:59:59"
    _buildEndOfDayISO(dStr) {
        const d = this._parseLocalDate(dStr);
        if (!d) return null;
        const yyyy = d.getFullYear();
        const MM   = String(d.getMonth() + 1).padStart(2, '0');
        const DD   = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${MM}-${DD}T23:59:59`;
    }

    _save() {
        if (this._suspendAutosave || this._isSubmitting || this._disconnected || this._leaving) return Promise.resolve(false);
        if (this._autosavePromise) {
            this._autosaveQueued = true;
            return this._autosavePromise;
        }
        this._autosavePromise = this._runAutosaves().finally(() => { this._autosavePromise = null; });
        return this._autosavePromise;
    }

    async _runAutosaves() {
        let saved = false;
        do {
            this._autosaveQueued = false;
            const revision = this._saveRevision;
            this._setSaveStatus('saving');
            try {
                const fields = this._collectFieldsStructured();
                const url = this.contextValue === "party" && this.hasPartyTokenValue
                    ? `/party/${encodeURIComponent(this.partyTokenValue)}/fields`
                    : `/party_drafts/${encodeURIComponent(this.draftIdValue)}`;
                const response = await this._saveRequest(url, {
                    method: "PATCH",
                    headers: this._headers(),
                    body: JSON.stringify({ fields_json: fields })
                });
                const payload = await response.json().catch(() => null);
                saved = this._saveResponseSucceeded(response, payload);
                if (!saved && this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('details_autosave_failed', 'save_failed');
                if (revision === this._saveRevision && !this._isSubmitting) {
                    this._setSaveStatus(saved ? 'saved' : 'error', this._autosaveErrorMessage(response, payload));
                }
            } catch (_) {
                saved = false;
                if (this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('details_autosave_failed', 'network');
                if (revision === this._saveRevision && !this._isSubmitting) this._setSaveStatus('error');
            }
        } while (this._autosaveQueued && !this._disconnected && !this._isSubmitting && !this._suspendAutosave);
        return saved;
    }

    _autosaveErrorMessage(response, payload) {
        this._setupSignInRequired = response.status === 401 && payload?.authentication_required;
        if (this._setupSignInRequired) return SETUP_SIGN_IN_MESSAGE;
        if (payload?.code === 'setup_csrf_expired') {
            return 'Your session could not be refreshed. Your edits are still here. Keep this page open and choose Try again.';
        }
        if (response.status === 422 && payload?.errors?.includes('party_end must be after or equal to party_start.')) {
            return 'Changes not saved. Set the end time after the start time, then try again.';
        }
        if ([401, 403, 404, 410].includes(response.status) || response.redirected) {
            return 'Changes not saved. Your session or invitation may have expired. Keep this page open while you check your sign-in.';
        }
        return 'Changes not saved. Your edits are still on this page. Check your details and connection, then try again.';
    }

    _saveResponseSucceeded(response, payload) {
        return response.ok && !response.redirected && payload !== null &&
            typeof payload === 'object' && !Array.isArray(payload) && payload.ok !== false && !payload.errors;
    }

    _setSaveStatus(state, message) {
        if (this._disconnected || !this.hasSaveStatusTarget) return;
        const messages = {
            pending: 'Unsaved changes',
            saving: 'Saving changes…',
            saved: 'Changes saved',
            error: 'Changes not saved. Your edits are still on this page. Check your connection and try again.'
        };
        this.saveStatusTarget.hidden = false;
        this.saveStatusTarget.dataset.state = state;
        this.saveStatusTarget.classList.toggle('text-danger', state === 'error');
        this.saveMessageTarget.textContent = message && state === 'error' ? message : messages[state];
        this.retrySaveTarget.hidden = state !== 'error';
        if (this.hasSignInRecoveryTarget) this.signInRecoveryTarget.hidden = state !== 'error' || !this._setupSignInRequired;
    }

    retrySave(event) {
        event?.preventDefault();
        this._saveDebounced?.cancel();
        return this._save();
    }

    async signInToSave(event) {
        event?.preventDefault();
        if (await signInForPartySetup(Swal) && !this._disconnected) await this.retrySave();
    }

    _restoreFormInteraction() {
        if (this._formInertBeforeSubmit !== undefined) {
            this.formTarget.inert = this._formInertBeforeSubmit;
            this._formInertBeforeSubmit = undefined;
        }
    }

    // continue(e) { ... } -- removed; submission is handled by JV onSuccess handler.

    async _submit(e) {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (this._isSubmitting || this._goingBack || this._leaving) return;

        if (this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('details_continue_attempt');

        // Guard: end >= start
        let fields = this._collectFieldsStructured();
        const sISO = fields?.party?.party_start || null;
        const eISO = fields?.party?.party_end   || null;
        if (eISO && sISO && new Date(eISO) < new Date(sISO)) {
            if (this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('details_validation_blocked', 'invalid_time_range');
            this._toast('End time must be after the start time.', 'error');
            try { this.validator?.revalidateField?.('#endTime'); } catch(_) {}
            return;
        }

        this._isSubmitting = true;
        // Prevent editing a snapshot while its final save is in flight. Unlike
        // disabled inputs, inert controls remain present in FormData.
        this._formInertBeforeSubmit = Boolean(this.formTarget.inert);
        this.formTarget.inert = true;
        this._saveDebounced?.cancel();
        this._autosaveQueued = false;
        this._toast(this.contextValue === "party" ? "Saving changes…" : "Saving details…", "info", { persist: true });

        try {
            // Finish any older write before the final snapshot; never let a late
            // autosave overwrite the successful Continue / Save changes request.
            await this._autosavePromise;
            if (this._disconnected) return;
            fields = this._collectFieldsStructured();
            if (fields.party?.party_end && fields.party?.party_start &&
                new Date(fields.party.party_end) < new Date(fields.party.party_start)) {
                this._setSaveStatus('error', 'Changes not saved. Set the end time after the start time, then try again.');
                this._toast('End time must be after the start time.', 'error');
                try { this.validator?.revalidateField?.('#endTime'); } catch (_) {}
                return;
            }
            if (this.contextValue === "party" && this.hasPartyTokenValue) {
                // --- EDIT EXISTING PARTY ---
                const res = await fetch(`/party/${this.partyTokenValue}/fields`, {
                    method: 'PATCH',
                    headers: this._headers(),
                    body: JSON.stringify({ fields_json: fields })
                });

                const payload = await res.json().catch(() => null);
                if (this._disconnected) return;
                if (!this._saveResponseSucceeded(res, payload)) {
                    this._setSaveStatus('error', this._autosaveErrorMessage(res, payload));
                    this._toast(payload?.errors?.[0] || 'Save failed', 'error');
                    return;
                }

                this._toast("Saved!", "success", { delayClose: 600 });
                this._leaving = true;
                // give the toast a tick to render, then bounce
                setTimeout(() => {
                    try { Swal.close?.(); } catch(_) {}
                    window.location.assign(`/dashboard/manage/${encodeURIComponent(this.partyTokenValue)}`);
                }, 150);
                return; // IMPORTANT: stop running the rest
            }

            // --- DRAFT FLOW (CREATE WIZARD) ---
            const res = await this._saveRequest(`/party_drafts/${this.draftIdValue}`, {
                method: 'PATCH',
                headers: this._headers(),
                body: JSON.stringify({ fields_json: fields, status: 'charity' })
            });

            const payload = await res.json().catch(() => null);
            if (this._disconnected) return;
            if (!this._saveResponseSucceeded(res, payload)) {
                window.echoageSetupDiagnostics?.report?.('details_continue_failed', 'save_failed');
                this._setSaveStatus('error', this._autosaveErrorMessage(res, payload));
                this._toast(payload?.errors?.[0] || (payload?.authentication_required ? 'Your edits are safe. Choose Sign in to continue.' : 'Unable to save. Please try again.'), 'error');
                return;
            }

            this._leaving = true;
            if (payload.authentication_required && payload.signup_url) {
                this._toast('Your party details are saved.', 'success', { delayClose: 400 });
                setTimeout(() => {
                    try { Swal.close?.(); } catch(_) {}
                    window.location.assign(payload.signup_url);
                }, 150);
                return;
            }

            this._toast('Preparing charity selection…', 'success', { delayClose: 600 });
            const nextUrl = this.anonymousValue
                ? `/party/charity?draft_ref=${encodeURIComponent(this.draftIdValue)}`
                : ((this.hasDraftSgidValue && this.draftSgidValue)
                    ? `/party/charity?draft_sgid=${encodeURIComponent(this.draftSgidValue)}`
                    : `/party/charity?draft_id=${encodeURIComponent(this.draftIdValue)}`);
            setTimeout(() => {
                try { Swal.close?.(); } catch(_) {}
                window.location.assign(nextUrl);
            }, 150);

        } catch (err) {
            // If navigation fires, some browsers surface AbortError—this keeps UX calm.
            if (err?.name !== 'AbortError') {
                if (this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('details_network_failed', 'network');
                this._setSaveStatus('error');
                this._toast('Network error, please try again', 'error');
            }
        } finally {
            this._isSubmitting = false;
            if (!this._leaving) this._restoreFormInteraction();
        }
    }
    _saveRequest(url, options) {
        if (this.contextValue === 'party') return fetch(url, options);
        return partySetupRequest(url, options, { requiresAccount: !this.anonymousValue });
    }

    _headers() {
        return {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
    }

    _toast(title, icon = 'info', opts = {}) {
        const { persist = false, delayClose = 0 } = opts;
        Swal.fire({
            toast: true,
            icon,
            title,
            position: 'bottom-end',
            showConfirmButton: false,
            timer: persist ? undefined : 2200,
            timerProgressBar: !persist,
        });
        if (persist && delayClose) {
            setTimeout(() => Swal.close(), delayClose);
        }
    }

    _debounce(fn, ms) {
        let t;
        const debounced = (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
        debounced.cancel = () => clearTimeout(t);
        return debounced;
    }

    reindexChildren() {
        const entries = this.childContainerTarget.querySelectorAll('[data-invitation-form-target="childEntry"]');

        entries.forEach((entry, newIndex) => {
            const oldIndex = parseInt((entry.id || '').replace('childEntry_', ''), 10);
            if (Number.isNaN(oldIndex)) return;

            // a) Remove old JV bindings for the IDs we’re about to change
            const oldNameId = `celebrateForChild_${oldIndex}`;
            const oldBdayId = `birthdayDateChild_${oldIndex}`;
            const oldGenId  = `genderChild_${oldIndex}`;
            try { this.validator.removeField?.(`#${oldNameId}`); } catch(_) {}
            try { this.validator.removeField?.(`#${oldBdayId}`); } catch(_) {}
            try { this.validator.removeField?.(`#${oldGenId}`); } catch(_) {}

            // b) Update container id
            entry.id = `childEntry_${newIndex}`;

            // c) Rename inputs/selects + error div
            const nameEl = entry.querySelector(`input[name="celebrateForChild_${oldIndex}"]`);
            if (nameEl) { nameEl.id = `celebrateForChild_${newIndex}`; nameEl.name = `celebrateForChild_${newIndex}`; }

            const bdayEl = entry.querySelector(`input[name="birthdayDateChild_${oldIndex}"]`);
            if (bdayEl) { bdayEl.id = `birthdayDateChild_${newIndex}`; bdayEl.name = `birthdayDateChild_${newIndex}`; }

            const genEl  = entry.querySelector(`select[name="genderChild_${oldIndex}"]`);
            if (genEl)  { genEl.id = `genderChild_${newIndex}`; genEl.name = `genderChild_${newIndex}`; }

            const genErr = entry.querySelector(`#genderChild_${oldIndex}Error`);
            if (genErr) { genErr.id = `genderChild_${newIndex}Error`; }

            // d) Refresh label text
            const label = entry.querySelector('label[for^="celebrateForChild_"]');
            if (label) {
                const word = this.ordinalWords[newIndex] || `Child ${newIndex + 1}`;
                label.textContent = `${word} child *`;
                // keep label “for” in sync if present
                label.setAttribute('for', `celebrateForChild_${newIndex}`);
            }

            // e) (Optional) keep data-target-id in sync if you still use it anywhere else
            const delLink = entry.querySelector('[data-action="click->invitation-form#removeChild"]');
            if (delLink) delLink.dataset.targetId = `childEntry_${newIndex}`;

            // f) Re-add JV rules for the NEW IDs
            if (nameEl) this.validator.addField(`#celebrateForChild_${newIndex}`, [
                { rule: 'required', errorMessage: "The celebrant's name is required." }
            ]);
            if (bdayEl) this.validator.addField(`#birthdayDateChild_${newIndex}`, [
                { rule: 'required', errorMessage: 'The birthday is required.' }
            ]);
            if (genEl)  this.validator.addField(`#genderChild_${newIndex}`, [
                { rule: 'required', errorMessage: 'The gender is required.' }
            ], { errorLabelContainer: `#genderChild_${newIndex}Error` });
        });
    }
}
